import React, { useState, useRef, useEffect } from 'react'

/**
 * Переиспользуемый аккордеон для секций настроек ЗП.
 * 
 * Props:
 *  - icon: ReactNode (SVG иконка)
 *  - title: string
 *  - subtitle: string (мелкое описание)
 *  - preview: string (значение справа, например "1700₽/см")
 *  - defaultOpen: boolean
 *  - accentColor: string (CSS color)
 *  - statusDot: 'green' | 'gray' | 'orange' | null
 *  - children: ReactNode
 *  - dirty: boolean — есть несохранённые изменения
 */
export default function SalaryAccordion({
  icon,
  title,
  subtitle,
  preview,
  defaultOpen = false,
  accentColor = 'var(--wb-primary)',
  statusDot = null,
  statusDotTooltip = null,
  children,
  dirty = false,
  ...props
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const [isHovered, setIsHovered] = useState(false)
  const contentRef = useRef(null)
  const [contentHeight, setContentHeight] = useState(0)

  useEffect(() => {
    if (contentRef.current) {
      setContentHeight(contentRef.current.scrollHeight)
    }
  }, [isOpen, children])

  // Пересчитывать высоту при изменении children
  useEffect(() => {
    if (!isOpen || !contentRef.current) return
    const observer = new ResizeObserver(() => {
      if (contentRef.current) {
        setContentHeight(contentRef.current.scrollHeight)
      }
    })
    observer.observe(contentRef.current)
    return () => observer.disconnect()
  }, [isOpen])

  const dotColor = dirty
    ? 'var(--warning, #F59E0B)'
    : statusDot === 'green'
    ? 'var(--success, #10B981)'
    : statusDot === 'gray'
    ? 'var(--text-tertiary, #9CA3AF)'
    : null

  const dotTooltip = dirty
    ? 'Есть несохранённые изменения'
    : (statusDotTooltip || (statusDot === 'green' ? 'Включено' : statusDot === 'gray' ? 'Выключено' : null))

  return (
    <div
      className="salary-accordion"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      {...props}
      style={{
        borderRadius: '16px',
        border: isOpen
          ? `1.5px solid color-mix(in srgb, ${accentColor} 30%, var(--border-color))`
          : (isHovered
              ? `1.5px solid color-mix(in srgb, ${accentColor} 25%, var(--border-color))`
              : '1.5px solid var(--border-color)'),
        background: isHovered && !isOpen
          ? 'var(--surface-hover, rgba(255, 255, 255, 0.02))'
          : 'var(--surface-color)',
        transform: isHovered ? 'translateY(-1px)' : 'none',
        transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), background 0.2s ease',
        overflow: 'hidden',
        boxShadow: isOpen
          ? `0 4px 20px color-mix(in srgb, ${accentColor} 8%, transparent)`
          : (isHovered ? `0 4px 14px color-mix(in srgb, ${accentColor} 6%, rgba(0, 0, 0, 0.05))` : 'none'),
      }}
    >
      {/* Header — кликабельный */}
      <button
        type="button"
        className="salary-accordion-header"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          width: '100%',
          padding: '18px 20px',
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'background 0.2s',
        }}
      >
        {/* Иконка */}
        <div
          style={{
            flexShrink: 0,
            width: '42px',
            height: '42px',
            borderRadius: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isOpen
              ? `linear-gradient(135deg, ${accentColor}, color-mix(in srgb, ${accentColor} 70%, #000))`
              : 'var(--surface-hover)',
            color: isOpen ? '#fff' : 'var(--text-secondary)',
            transition: 'all 0.3s ease',
          }}
        >
          {icon}
        </div>

        {/* Название + описание */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '15px',
              fontWeight: 700,
              color: isOpen ? accentColor : 'var(--text-primary)',
              transition: 'color 0.2s',
            }}
          >
            {title}
            {dotColor && (
              <span
                title={dotTooltip || undefined}
                aria-label={dotTooltip || undefined}
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: dotColor,
                  flexShrink: 0,
                  cursor: dotTooltip ? 'help' : 'default',
                  display: 'inline-block',
                }}
              />
            )}
          </div>
          {subtitle && (
            <div
              style={{
                fontSize: '12.5px',
                color: 'var(--text-secondary)',
                marginTop: '2px',
                lineHeight: '1.4',
              }}
            >
              {subtitle}
            </div>
          )}
        </div>

        {/* Preview (закрытое состояние) */}
        {!isOpen && preview && (
          <div
            style={{
              fontSize: '13px',
              color: 'var(--text-secondary)',
              fontWeight: 500,
              whiteSpace: 'nowrap',
              background: 'var(--surface-hover)',
              padding: '4px 10px',
              borderRadius: '8px',
            }}
          >
            {preview}
          </div>
        )}

        {/* Стрелка */}
        <div
          style={{
            flexShrink: 0,
            width: '28px',
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '8px',
            background: isOpen ? `color-mix(in srgb, ${accentColor} 10%, transparent)` : 'transparent',
            transition: 'all 0.3s ease',
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke={isOpen ? accentColor : 'var(--text-tertiary)'}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </button>

      {/* Content — анимированная область */}
      <div
        style={{
          maxHeight: isOpen ? `${contentHeight + 40}px` : '0px',
          opacity: isOpen ? 1 : 0,
          transition: 'max-height 0.35s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s ease',
          overflow: 'hidden',
        }}
      >
        <div
          ref={contentRef}
          style={{
            padding: '0 20px 20px',
            borderTop: '1px solid var(--border-color)',
            paddingTop: '20px',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  )
}
